import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form, Spin } from 'antd'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useStocksServiceGetMedicineEndpoint,
  useStocksServiceGetMedicineListEndpointKey,
  useStocksServiceUpdateMedicineEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { FormFooter, message, PageBreadcrumbs } from 'src/components'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

import { ProductFields } from '../components'

export const EditProductPage = () => {
  const { t, currentLang } = useLocales()
  const [form] = Form.useForm()
  const { id } = useParams()

  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { data, isLoading } = useStocksServiceGetMedicineEndpoint(
    { id: String(id) },
    [id],
    { enabled: !!id }
  )

  const { mutate, isPending } = useStocksServiceUpdateMedicineEndpoint({
    onError: (e: ApiError) => form.setFields(formErrorHandler(e.body.errors)),
    onSuccess: () => {
      message.success(t('common.save_success'))
      queryClient.invalidateQueries({
        queryKey: [useStocksServiceGetMedicineListEndpointKey]
      })
      navigate(paths.medicinesRegister.products.root)
    }
  })

  const onCancel = () => {
    form.resetFields()
    navigate(paths.medicinesRegister.products.root)
  }

  const onSubmit = async () => {
    const requestBody = await form.validateFields()

    mutate({
      requestBody: {
        ...requestBody,
        formId:
          requestBody.formId === data?.form?.name
            ? data?.form?.id
            : requestBody.formId,
        producerId:
          requestBody.producerId === data?.producer?.name
            ? data?.producer?.id
            : requestBody.producerId,
        internationalNameId:
          requestBody.internationalNameId ===
          `${data?.internationalName?.name} ${data?.doses}`
            ? data?.internationalName?.id
            : requestBody.internationalNameId,
        groupId:
          requestBody?.groupId ===
          data?.group?.translation?.[currentLang?.value]
            ? data?.group?.id
            : requestBody?.groupId.value
      },
      id: String(id)
    })
  }

  const breadcrumbItems = [
    {
      title: t('menu.medicines_register')
    },
    {
      title: t('medicine_register.products.title')
    },
    ...(data?.commercialName
      ? [
          {
            title: data?.commercialName
          }
        ]
      : [])
  ]

  useEffect(() => {
    if (data && id)
      form.setFieldsValue({
        ...data,
        prescriptionId: data.prescription?.id,
        formId: data.form?.name,
        producerId: data.producer?.name,
        internationalNameId: `${data.internationalName?.name} ${data?.doses}`,
        groupId: data?.group?.translation?.[currentLang?.value]
      })
  }, [data, id])

  return (
    <Spin spinning={isLoading}>
      <PageBreadcrumbs
        items={breadcrumbItems}
        title={data?.commercialName ?? ''}
      >
        <Flex vertical gap={24}>
          <Flex justify="space-between" gap={16}>
            <Button icon={<LeftOutlined />} onClick={onCancel}>
              {t('common.back')}
            </Button>
          </Flex>

          <Form form={form} layout="vertical" requiredMark={false}>
            <ProductFields form={form} hasDose={!!data?.doses} />

            <FormFooter
              onCancel={onCancel}
              onSave={onSubmit}
              saveConfirmTitle={`${t('common.edit')}?`}
              saveLabel={t('common.edit')}
              loading={isPending}
            />
          </Form>
        </Flex>
      </PageBreadcrumbs>
    </Spin>
  )
}
